import type { AppUserId, ContactMessageId } from "./ids";
import type { AudienceType, ServiceInterest } from "./submission";
import type { LanguageTag } from "./user";

export const CONTACT_MESSAGE_STATUSES = [
  "new",
  "in_progress",
  "answered",
  "spam",
  "archived",
] as const;
export type ContactMessageStatus = (typeof CONTACT_MESSAGE_STATUSES)[number];

/**
 * Public contact enquiry. Accepts anonymous submissions, so it carries abuse
 * metadata that the authenticated application form does not need.
 *
 * Only the four fields the brief names as the minimum -- name, email,
 * interest, message -- are required. Everything else is optional context.
 */
export interface ContactMessage {
  readonly id: ContactMessageId;
  readonly name: string;
  readonly email: string;
  readonly serviceInterest: ServiceInterest;
  readonly audienceType: AudienceType | null;
  readonly organization: string | null;
  readonly message: string;
  readonly locale: LanguageTag;
  readonly status: ContactMessageStatus;
  readonly consentToContact: boolean;
  readonly consentRecordedAt: Date;
  /** Set when a signed-in user submits the public form; null when anonymous. */
  readonly submittedByAppUserId: AppUserId | null;
  readonly createdAt: Date;
  readonly updatedAt: Date;
  /**
   * Salted hash of the request IP, kept for rate-limit forensics only.
   * The raw address is never stored.
   */
  readonly requestIpHash: string | null;
  readonly antiAbuseVerdict: AntiAbuseVerdict | null;
  readonly clientRequestId: string | null;
}

export interface AntiAbuseVerdict {
  readonly provider: string;
  readonly passed: boolean;
  readonly score: number | null;
  readonly checkedAt: Date;
}

export interface AdminContactMessageView {
  id: ContactMessageId;
  name: string;
  email: string;
  serviceInterest: ServiceInterest;
  audienceType: AudienceType | null;
  organization: string | null;
  message: string;
  locale: LanguageTag;
  status: ContactMessageStatus;
  createdAt: string;
  antiAbusePassed: boolean | null;
}

export const toAdminContactMessageView = (
  m: ContactMessage,
): AdminContactMessageView => ({
  id: m.id,
  name: m.name,
  email: m.email,
  serviceInterest: m.serviceInterest,
  audienceType: m.audienceType,
  organization: m.organization,
  message: m.message,
  locale: m.locale,
  status: m.status,
  createdAt: m.createdAt.toISOString(),
  antiAbusePassed: m.antiAbuseVerdict ? m.antiAbuseVerdict.passed : null,
});
